import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  VStack,
  Box,
  Text,
  Input,
  Button,
  Slider,
  Checkbox,
  Spinner,
  Flex,
  Badge,
} from '@chakra-ui/react';
import { useRoomStore } from '../../store';
import useSlugValidation from '../../hooks/useSlugValidation';
import { roomApi } from '../../services/api';
import { useUser } from '@clerk/clerk-react';
import AlertModal from '../AlertModal';
import { containsProfanity } from '../../utils/profanityFilter';
import { TAG_CATEGORIES, MAX_TAGS_PER_ROOM } from '../../data/roomTags';
import { APP_DOMAIN } from '../../constants/defaults';

// Helper function to convert name to slug
const nameToSlug = (name) => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

function RoomSettingsPanel({
  roomId,
  roomName: initialRoomName,
  onRoomNameChange,
  roomTags: initialTags = [],
  listedInDirectory: initialListed = true,
  hasPassword: initialHasPassword = false,
  onTagsChange,
  onListedInDirectoryChange,
}) {
  const { isSignedIn, user } = useUser();

  // Room settings from store
  const lightIntensity = useRoomStore((s) => s.lightIntensity);
  const setLightIntensity = useRoomStore((s) => s.setLightIntensity);
  const roomLiningVisible = useRoomStore((s) => s.roomLiningVisible);
  const setRoomLiningVisible = useRoomStore((s) => s.setRoomLiningVisible);

  // Room name state
  const [roomName, setRoomName] = useState(initialRoomName || '');
  // Track the last saved/loaded name so button grays out when name matches it
  const savedNameRef = useRef(initialRoomName || '');
  const [isSaving, setIsSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [profanityError, setProfanityError] = useState(null);

  // Tags and directory listing state
  const [tags, setTags] = useState(initialTags);
  const [listed, setListed] = useState(initialListed);

  // Password protection state
  const [passwordEnabled, setPasswordEnabled] = useState(initialHasPassword);
  const [password, setPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  // Auto-generate slug from name
  const slug = nameToSlug(roomName);

  // Real-time slug validation
  const slugValidation = useSlugValidation(slug, roomId);

  // Update local state when prop changes (e.g. room data loaded from backend)
  useEffect(() => {
    if (initialRoomName && initialRoomName !== roomName) {
      setRoomName(initialRoomName);
    }
    if (initialRoomName) {
      savedNameRef.current = initialRoomName;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRoomName]);

  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  useEffect(() => {
    setListed(initialListed);
  }, [initialListed]);

  // Debounced save for room name
  const saveRoomName = useCallback(async (newName, newSlug) => {
    if (!roomId || !newName.trim()) return;

    // Check slug validation before saving
    if (!slugValidation.isValid || !slugValidation.isAvailable) {
      return; // Don't save if slug is invalid
    }

    setIsSaving(true);
    try {
      const userInfo = { isSignedIn, user };
      await roomApi.updateRoom(roomId, {
        name: newName,
        slug: newSlug
      }, userInfo);

      // Notify parent of name change
      if (onRoomNameChange) {
        onRoomNameChange(newName, newSlug);
      }
      savedNameRef.current = newName;
    } catch (err) {
      console.error('Failed to save room name:', err);
      const errorMessage = err.response?.data?.error?.message || err.message || 'Failed to save room name';
      setAlertMessage({ message: errorMessage, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [roomId, isSignedIn, user, slugValidation.isValid, slugValidation.isAvailable, onRoomNameChange]);

  // Handle name change
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setRoomName(newName);
    if (profanityError) setProfanityError(null);
  };

  // Check for profanity on blur so we're not running it on every keystroke
  const handleNameBlur = (e) => {
    const name = e.target.value;
    if (name.trim() && containsProfanity(name)) {
      setProfanityError('Room name contains inappropriate language');
    } else {
      setProfanityError(null);
    }
  };

  // Save name button handler
  const handleSaveName = () => {
    if (roomName.trim() && slugValidation.isValid && slugValidation.isAvailable && !profanityError) {
      saveRoomName(roomName, slug);
    }
  };

  const hasNameChanged = roomName.trim() !== savedNameRef.current.trim();
  const canSave = hasNameChanged && roomName.trim() && !profanityError &&
    slugValidation.isValid && slugValidation.isAvailable &&
    !slugValidation.isChecking && !isSaving;

  // Light intensity labels
  const getLightLabel = (value) => {
    if (value <= 0.5) return 'Dim';
    if (value <= 0.8) return 'Low';
    if (value <= 1.2) return 'Medium';
    if (value <= 1.6) return 'Bright';
    return 'Very Bright';
  };

  // Directory listing toggle
  const handleListedChange = async (e) => {
    const newValue = !!e.checked;
    setListed(newValue);
    if (onListedInDirectoryChange) onListedInDirectoryChange(newValue);
    try {
      const userInfo = { isSignedIn, user };
      await roomApi.updateRoom(roomId, { listedInDirectory: newValue }, userInfo);
    } catch (err) {
      console.error('Failed to update directory listing:', err);
      // Revert on error
      setListed(!newValue);
      if (onListedInDirectoryChange) onListedInDirectoryChange(!newValue);
    }
  };

  // Password protection toggle
  const handlePasswordToggle = async (e) => {
    const enabled = !!e.checked;
    setPasswordEnabled(enabled);
    setPasswordSaved(false);
    if (!enabled) {
      // Remove password
      setPassword('');
      try {
        const userInfo = { isSignedIn, user };
        await roomApi.updateRoom(roomId, { password: null }, userInfo);
      } catch (err) {
        console.error('Failed to remove password:', err);
        setPasswordEnabled(true); // Revert on error
      }
    }
  };

  // Save password
  const handleSavePassword = async () => {
    if (!password.trim()) return;
    setIsSavingPassword(true);
    setPasswordSaved(false);
    try {
      const userInfo = { isSignedIn, user };
      await roomApi.updateRoom(roomId, { password: password.trim() }, userInfo);
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save password:', err);
      setAlertMessage({ message: 'Failed to save password', type: 'error' });
    } finally {
      setIsSavingPassword(false);
    }
  };

  // Tag toggle
  const handleTagToggle = async (tag) => {
    let newTags;
    if (tags.includes(tag)) {
      newTags = tags.filter(t => t !== tag);
    } else {
      if (tags.length >= MAX_TAGS_PER_ROOM) return;
      newTags = [...tags, tag];
    }
    setTags(newTags);
    if (onTagsChange) onTagsChange(newTags);
    try {
      const userInfo = { isSignedIn, user };
      await roomApi.updateRoom(roomId, { tags: newTags }, userInfo);
    } catch (err) {
      console.error('Failed to update tags:', err);
      // Revert on error
      setTags(tags);
      if (onTagsChange) onTagsChange(tags);
    }
  };

  const atMaxTags = tags.length >= MAX_TAGS_PER_ROOM;

  return (
    <VStack gap={6} align="stretch" width="100%" p={2}>
      {/* Room Name Section */}
      <Box>
        <Text fontSize="sm" fontWeight="600" color="gray.300" mb={2}>
          Room Name
        </Text>
        <Input
          value={roomName}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          placeholder="Name your room"
          bg="gray.700"
          border="1px solid"
          borderColor="gray.600"
          color="white"
          _placeholder={{ color: 'gray.500' }}
          _focus={{ borderColor: '#26EF96', boxShadow: '0 0 0 1px #26EF96' }}
          maxLength={100}
        />

        {/* URL Preview */}
        <Box
          mt={2}
          p={2}
          bg="gray.800"
          borderRadius="4px"
          fontFamily="mono"
          fontSize="sm"
        >
          <Text color="gray.500" as="span">{APP_DOMAIN}/</Text>
          <Text color={slug ? 'white' : 'gray.600'} as="span">
            {slug || 'your-room'}
          </Text>
        </Box>

        {/* Profanity validation */}
        {profanityError && (
          <Text fontSize="xs" color="red.400" mt={1}>
            {profanityError}
          </Text>
        )}

        {/* Validation status */}
        {!profanityError && slugValidation.isChecking && (
          <Text fontSize="xs" color="gray.400" mt={1}>
            <Spinner size="xs" mr={1} /> Checking availability...
          </Text>
        )}
        {!profanityError && !slugValidation.isChecking && slugValidation.error && (
          <Text fontSize="xs" color="red.400" mt={1}>
            {slugValidation.error}
          </Text>
        )}
        {!profanityError && !slugValidation.isChecking && slugValidation.isAvailable && slug && (
          <Text fontSize="xs" color="green.400" mt={1}>
            Available
          </Text>
        )}
        <Button
          mt={2}
          size="sm"
          bg={canSave ? '#26EF96' : 'gray.600'}
          color={canSave ? 'gray.900' : 'gray.400'}
          _hover={canSave ? { bg: '#1ed980' } : {}}
          onClick={handleSaveName}
          disabled={!canSave}
          loading={isSaving}
          width="100%"
        >
          Save Name
        </Button>
      </Box>

      {/* Lighting Section */}
      <Box>
        <Text fontSize="sm" fontWeight="600" color="gray.300" mb={2}>
          Lighting: {getLightLabel(lightIntensity)}
        </Text>
        <Slider.Root
          value={[lightIntensity]}
          onValueChange={(details) => setLightIntensity(details.value[0])}
          min={0.3}
          max={2.0}
          step={0.1}
          width="100%"
        >
          <Slider.Control>
            <Slider.Track bg="gray.600">
              <Slider.Range bg="#26EF96" />
            </Slider.Track>
            <Slider.Thumbs>
              <Slider.Thumb index={0} boxSize={5} bg="#26EF96" />
            </Slider.Thumbs>
          </Slider.Control>
        </Slider.Root>
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Text fontSize="xs" color="gray.500">Dim</Text>
          <Text fontSize="xs" color="gray.500">Bright</Text>
        </Box>
      </Box>

      {/* Room Lining Section */}
      <Box>
        <Checkbox.Root
          variant="solid"
          checked={roomLiningVisible}
          onCheckedChange={(e) => setRoomLiningVisible(!!e.checked)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>
            <Text fontSize="sm" color="gray.300">
              Show room outline
            </Text>
          </Checkbox.Label>
        </Checkbox.Root>
        <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
          Display grid lines around the room edges
        </Text>
      </Box>

      {/* Directory Listing Section */}
      <Box>
        <Checkbox.Root
          variant="solid"
          checked={listed}
          onCheckedChange={handleListedChange}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>
            <Text fontSize="sm" color="gray.300">
              List in All Rooms directory
            </Text>
          </Checkbox.Label>
        </Checkbox.Root>
        <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
          When checked, your room appears on the public All Rooms page
        </Text>
      </Box>

      {/* Password Protection Section */}
      <Box>
        <Checkbox.Root
          variant="solid"
          checked={passwordEnabled}
          onCheckedChange={handlePasswordToggle}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>
            <Text fontSize="sm" color="gray.300">
              Password protect this room
            </Text>
          </Checkbox.Label>
        </Checkbox.Root>
        <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
          Visitors must enter a password to view your room
        </Text>
        {passwordEnabled && (
          <Box mt={3}>
            <Input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPasswordSaved(false); }}
              placeholder={initialHasPassword ? 'Enter new password' : 'Enter room password'}
              bg="gray.700"
              border="1px solid"
              borderColor="gray.600"
              color="white"
              _placeholder={{ color: 'gray.500' }}
              _focus={{ borderColor: '#26EF96', boxShadow: '0 0 0 1px #26EF96' }}
              maxLength={100}
              size="sm"
            />
            <Button
              mt={2}
              size="sm"
              bg={password.trim() ? '#26EF96' : 'gray.600'}
              color={password.trim() ? 'gray.900' : 'gray.400'}
              _hover={password.trim() ? { bg: '#1ed980' } : {}}
              onClick={handleSavePassword}
              disabled={!password.trim() || isSavingPassword}
              loading={isSavingPassword}
              width="100%"
            >
              {passwordSaved ? 'Password Saved' : 'Save Password'}
            </Button>
            {initialHasPassword && !password.trim() && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                Room currently has a password set. Enter a new one to change it.
              </Text>
            )}
          </Box>
        )}
      </Box>

      {/* Tags Section */}
      <Box>
        <Text fontSize="sm" fontWeight="600" color="gray.300" mb={2}>
          Tags ({tags.length}/{MAX_TAGS_PER_ROOM})
        </Text>

        {/* Selected tags */}
        {tags.length > 0 && (
          <Flex gap={2} flexWrap="wrap" mb={3}>
            {tags.map(tag => (
              <Badge
                key={tag}
                bg="#26EF96"
                color="gray.900"
                px={2}
                py={1}
                borderRadius="full"
                fontSize="xs"
                cursor="pointer"
                onClick={() => handleTagToggle(tag)}
                _hover={{ opacity: 0.8 }}
              >
                {tag} x
              </Badge>
            ))}
          </Flex>
        )}

        {/* Tag categories */}
        {Object.entries(TAG_CATEGORIES).map(([category, categoryTags]) => (
          <Box key={category} mb={3}>
            <Text fontSize="xs" color="gray.400" mb={1} fontWeight="600">
              {category}
            </Text>
            <Flex gap={1} flexWrap="wrap">
              {categoryTags.map(tag => {
                const isSelected = tags.includes(tag);
                const isDisabled = atMaxTags && !isSelected;
                return (
                  <Badge
                    key={tag}
                    bg={isSelected ? '#26EF96' : 'gray.700'}
                    color={isSelected ? 'gray.900' : isDisabled ? 'gray.600' : 'gray.300'}
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    fontSize="xs"
                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                    opacity={isDisabled ? 0.4 : 1}
                    onClick={() => !isDisabled && handleTagToggle(tag)}
                    _hover={!isDisabled ? { opacity: 0.8 } : {}}
                    transition="all 0.15s"
                  >
                    {tag}
                  </Badge>
                );
              })}
            </Flex>
          </Box>
        ))}
      </Box>

      {/* Alert Modal for errors */}
      {alertMessage && (
        <AlertModal
          message={alertMessage.message}
          type={alertMessage.type}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </VStack>
  );
}

export default RoomSettingsPanel;
